import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  RefreshCw,
  Users,
  Filter,
  X,
  Phone,
  Mail,
  MapPin,
  Grid3x3,
  List,
  Grid2x2
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { getCustomers, getCustomerStatistics, deleteCustomer } from '../api/customers';
import Pagination from '../components/common/Pagination';
import LoadingSpinner from '../components/common/LoadingSpinner';

const CustomersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);

  // View mode state
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('customerViewMode') || 'grid';
  });

  // Save view mode to localStorage
  useEffect(() => {
    localStorage.setItem('customerViewMode', viewMode);
  }, [viewMode]);

  // Filter state
  const [filters, setFilters] = useState({
    page: 1,
    limit: 12,
    search: '',
    customerType: '',
    isActive: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
    hasMore: false
  });

  const [showFilters, setShowFilters] = useState(false);

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCustomers(filters);
      setCustomers(response.customers || []);
      setPagination(
        response.pagination || {
          page: 1,
          limit: 12,
          total: 0,
          totalPages: 0,
          hasMore: false
        }
      );
    } catch (error) {
      toast.error('Failed to fetch customers');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      const stats = await getCustomerStatistics();
      setStatistics(stats);
    } catch (error) {}
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetchStatistics();
  }, [fetchCustomers, fetchStatistics]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 12,
      search: '',
      customerType: '',
      isActive: '',
      sortBy: 'created_at',
      sortOrder: 'DESC'
    });
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    try {
      await deleteCustomer(id);
      toast.success('Customer deleted successfully');
      fetchCustomers();
      fetchStatistics();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete customer';
      toast.error(errorMessage);
    }
  };

  // Check if user can edit
  const canEdit =
    user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'system_admin';

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  // Get customer type badge color
  const getTypeColor = (type) => {
    switch (type) {
      case 'retail':
        return 'bg-blue-100 text-blue-800';
      case 'wholesaler':
        return 'bg-purple-100 text-purple-800';
      case 'contractor':
        return 'bg-green-100 text-green-800';
      case 'corporate':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get customer display type (wholesaler takes precedence)
  const getCustomerDisplayType = (customer) => {
    return customer.is_wholesaler ? 'wholesaler' : customer.customer_type;
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite">
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-primary-black">
                Customer Management
              </h1>
              <p className="text-sm md:text-base text-gray-600 mt-1">
                Manage your customer database
              </p>
            </div>
            {canEdit && (
              <button
                onClick={() => navigate('/customers/new')}
                className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors min-h-[44px]"
              >
                <Plus className="w-5 h-5" />
                Add Customer
              </button>
            )}
          </div>

          {/* Statistics */}
          {statistics && (
            <div
              className={`grid ${canEdit ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2'} gap-3 md:gap-4 mb-6`}
            >
              <div className="bg-white rounded-lg shadow-md p-4 border border-blue-500">
                <h3 className="text-xs md:text-sm font-medium text-gray-600 mb-1">
                  Total Customers
                </h3>
                <p className="text-xl md:text-2xl font-bold text-primary-black">
                  {statistics.total_customers}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 border border-green-500">
                <h3 className="text-xs md:text-sm font-medium text-gray-600 mb-1">Active</h3>
                <p className="text-xl md:text-2xl font-bold text-primary-black">
                  {statistics.active_customers}
                </p>
              </div>
              {canEdit && (
                <>
                  <div className="bg-white rounded-lg shadow-md p-4 border border-purple-500">
                    <h3 className="text-xs md:text-sm font-medium text-gray-600 mb-1">Wholesale</h3>
                    <p className="text-xl md:text-2xl font-bold text-primary-black">
                      {statistics.wholesale_customers}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg shadow-md p-4 border border-accent-gold">
                    <h3 className="text-xs md:text-sm font-medium text-gray-600 mb-1">
                      Total Sales
                    </h3>
                    <p className="text-xl md:text-2xl font-bold text-primary-black">
                      {formatCurrency(statistics.total_sales_value || 0)}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Search, Filter and View Mode Bar */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex flex-col md:flex-row gap-3 md:gap-4">
              <div className="flex-1 relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by name, phone, email..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                />
              </div>
              <div className="flex gap-2">
                {/* View Mode Toggle */}
                <div className="flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-1 border border-gray-300 rounded-lg bg-gray-50">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1 sm:p-1.5 rounded transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center ${
                      viewMode === 'list'
                        ? 'bg-accent-gold text-primary-black'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="List view"
                  >
                    <List className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('compact')}
                    className={`p-1 sm:p-1.5 rounded transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center ${
                      viewMode === 'compact'
                        ? 'bg-accent-gold text-primary-black'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Compact view"
                  >
                    <Grid2x2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1 sm:p-1.5 rounded transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center ${
                      viewMode === 'grid'
                        ? 'bg-accent-gold text-primary-black'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Grid view"
                  >
                    <Grid3x3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[36px]"
                >
                  <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Filters</span>
                </button>
                <button
                  onClick={() => {
                    fetchCustomers();
                    fetchStatistics();
                  }}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[36px]"
                >
                  <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer Type
                    </label>
                    <select
                      value={filters.customerType}
                      onChange={(e) => handleFilterChange('customerType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    >
                      <option value="">All Types</option>
                      <option value="retail">Retail</option>
                      <option value="wholesale">Wholesale</option>
                      <option value="contractor">Contractor</option>
                      <option value="corporate">Corporate</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={filters.isActive}
                      onChange={(e) => handleFilterChange('isActive', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    >
                      <option value="">All Status</option>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                    <select
                      value={filters.sortBy}
                      onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    >
                      <option value="created_at">Date Created</option>
                      <option value="full_name">Name</option>
                      <option value="total_purchases">Total Purchases</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
                >
                  <X className="w-4 h-4" />
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Customers Display */}
        {loading ? (
          <LoadingSpinner message="Loading customers..." className="py-20" />
        ) : customers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Customers Found</h3>
            <p className="text-gray-500 mb-6">
              {filters.search || filters.customerType
                ? 'Try adjusting your filters'
                : 'Get started by adding your first customer'}
            </p>
            {canEdit && !filters.search && !filters.customerType && (
              <button
                onClick={() => navigate('/customers/new')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add Customer
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Grid View */}
            {viewMode === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {customers.map((customer) => (
                  <div
                    key={customer.id}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/customers/${customer.id}`)}
                  >
                    <div className="p-4">
                      {/* Customer Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-primary-black mb-1">
                            {customer.full_name}
                          </h3>
                          <p className="text-xs text-gray-500">{customer.customer_code}</p>
                        </div>
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded ${getTypeColor(getCustomerDisplayType(customer))}`}
                        >
                          {getCustomerDisplayType(customer)}
                        </span>
                      </div>

                      {/* Contact Info */}
                      <div className="space-y-2 mb-3 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span>{customer.phone}</span>
                        </div>
                        {customer.email && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span className="truncate">{customer.email}</span>
                          </div>
                        )}
                        {customer.city && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPin className="w-4 h-4" />
                            <span>
                              {customer.city}
                              {customer.state && `, ${customer.state}`}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Financial Info */}
                      <div className="flex items-center justify-between mb-3 pb-3 border-b">
                        <div>
                          <p className="text-xs text-gray-500">Total Purchases</p>
                          <p className="text-sm font-bold text-accent-gold">
                            {formatCurrency(customer.total_purchases || 0)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Balance</p>
                          <p
                            className={`text-sm font-bold ${
                              customer.current_balance < 0 ? 'text-red-600' : 'text-green-600'
                            }`}
                          >
                            {formatCurrency(customer.current_balance || 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Compact View */}
            {viewMode === 'compact' && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {customers.map((customer) => (
                  <div
                    key={customer.id}
                    className="bg-white rounded-lg shadow-md p-3 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/customers/${customer.id}`)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-primary-black truncate">
                        {customer.full_name}
                      </h3>
                      <span
                        className={`px-1 py-0.5 text-xs rounded ${getTypeColor(getCustomerDisplayType(customer))}`}
                      >
                        {getCustomerDisplayType(customer)?.[0]?.toUpperCase() || 'N/A'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{customer.phone}</p>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs text-gray-500">Sales</p>
                        <p className="text-xs font-bold text-accent-gold">
                          {formatCurrency(customer.total_purchases || 0)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Bal</p>
                        <p
                          className={`text-xs font-bold ${
                            customer.current_balance < 0 ? 'text-red-600' : 'text-green-600'
                          }`}
                        >
                          {formatCurrency(customer.current_balance || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <>
                {/* Mobile Card View */}
                <div className="block md:hidden space-y-3">
                  {customers.map((customer) => (
                    <div key={customer.id} className="bg-white rounded-lg shadow-md p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="h-12 w-12 flex-shrink-0 bg-accent-gold rounded-full flex items-center justify-center">
                          <Users className="w-6 h-6 text-primary-black" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 mb-1">
                            {customer.full_name}
                          </h3>
                          <div className="text-xs font-mono font-semibold px-2 py-0.5 rounded bg-gradient-to-r from-accent-gold to-accent-goldDark text-primary-black inline-block">
                            {customer.customer_code}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mb-3 pb-3 border-b border-gray-100">
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Phone</p>
                          <p className="text-sm font-medium text-gray-900">{customer.phone}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Type</p>
                          <span
                            className={`px-2 py-0.5 text-xs font-semibold rounded ${getTypeColor(getCustomerDisplayType(customer))}`}
                          >
                            {getCustomerDisplayType(customer)}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Purchases</p>
                          <p className="text-sm font-bold text-accent-gold">
                            {formatCurrency(customer.total_purchases || 0)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Balance</p>
                          <p
                            className={`text-sm font-bold ${
                              customer.current_balance < 0 ? 'text-red-600' : 'text-green-600'
                            }`}
                          >
                            {formatCurrency(customer.current_balance || 0)}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/customers/${customer.id}`)}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Customer
                          </th>
                          <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contact
                          </th>
                          <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-4 md:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Purchases
                          </th>
                          <th className="px-4 md:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Balance
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {customers.map((customer) => (
                          <tr
                            key={customer.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => navigate(`/customers/${customer.id}`)}
                          >
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                              <div>
                                <p className="text-sm font-semibold text-primary-black">
                                  {customer.full_name}
                                </p>
                                <p className="text-xs text-gray-500">{customer.customer_code}</p>
                              </div>
                            </td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                              <div className="text-sm">
                                <p className="text-gray-900">{customer.phone}</p>
                                {customer.email && (
                                  <p className="text-xs text-gray-500 truncate max-w-[150px] lg:max-w-xs">
                                    {customer.email}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 text-xs font-semibold rounded ${getTypeColor(getCustomerDisplayType(customer))}`}
                              >
                                {getCustomerDisplayType(customer)}
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap text-right">
                              <span className="text-sm font-semibold text-accent-gold">
                                {formatCurrency(customer.total_purchases || 0)}
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap text-right">
                              <span
                                className={`text-sm font-semibold ${
                                  customer.current_balance < 0 ? 'text-red-600' : 'text-green-600'
                                }`}
                              >
                                {formatCurrency(customer.current_balance || 0)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* Pagination */}
            <div className="mt-6">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                totalItems={pagination.total}
                itemsPerPage={filters.limit}
                onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
                itemLabel="customers"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomersPage;
